import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Footprints } from "lucide-react";
import { MdPersonPinCircle } from "react-icons/md";
import { TbExchange } from "react-icons/tb";

const StepsWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const stepsTab = tabs.find((tab) => tab.id === "steps");
    const widget = stepsTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <motion.div className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-2xl p-5 shadow-lg border border-green-100 mb-4 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMxLjIgMCAyLjMuNSAzLjIgMS4zLjguOCAxLjMgMS45IDEuMyAzLjIgMCAxLjItLjUgMi4zLTEuMyAzLjItLjguOC0xLjkgMS4zLTMuMiAxLjMtMS4yIDAtMi4zLS41LTMuMi0xLjMtLjgtLjgtMS4zLTEuOS0xLjMtMy4yIDAtMS4yLjUtMi4zIDEuMy0zLjIuOC0uOCAxLjktMS4zIDMuMi0xLjN6IiBzdHJva2U9InJnYmEoMCwwLDAsMC4wMikiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-10"></div>

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="bg-green-100 p-1.5 rounded-lg">
                            <MdPersonPinCircle className="w-5 h-5 text-green-500" />
                        </div>
                        <h5 className="text-green-800 font-semibold">{widget.title}</h5>
                    </div>
                    <div className="flex items-center">
                        <motion.span
                            className="text-xs bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1 border border-green-200/50"
                            initial={{ x: 20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                            {widget.steps}
                        </motion.span>
                    </div>
                </div>

                {/* Circular progress */}
                <div className="relative h-36 mb-4 overflow-hidden rounded-xl bg-gradient-to-b from-green-50 to-emerald-50/50 flex items-center justify-center border border-green-100/50 shadow-inner">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,197,94,0.05),transparent_70%)]"></div>

                    <div className="relative">
                        <svg width="120" height="120" viewBox="0 0 120 120">
                            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#22c55e" floodOpacity="0.3" />
                            </filter>

                            <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />

                            <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#4ade80" />
                                    <stop offset="100%" stopColor="#22c55e" />
                                </linearGradient>
                            </defs>

                            <motion.circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="url(#progressGradient)"
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray="339.3"
                                filter="url(#shadow)"
                                initial={{ strokeDashoffset: 339.3 }}
                                animate={{ strokeDashoffset: 118.8 }}
                                transition={{ duration: 1.8, ease: "easeInOut" }}
                            />

                            <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}>
                                <circle cx="60" cy="6" r="3" fill="#22c55e" />
                                <circle cx="114" cy="60" r="3" fill="#22c55e" />
                                <circle cx="60" cy="114" r="3" fill="#e2e8f0" />
                                <circle cx="6" cy="60" r="3" fill="#e2e8f0" />
                            </motion.g>
                        </svg>

                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.div
                                className="flex flex-col items-center"
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.5, duration: 0.7, type: "spring", stiffness: 100 }}
                            >
                                <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-green-600 to-green-700">{widget.stepsNumber}</span>
                                <motion.div
                                    className="text-xs text-green-600 flex items-center gap-1 mt-1"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.8, duration: 0.5 }}
                                >
                                    <span>{widget.goal}</span>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Animated icon */}
                    <motion.div
                        className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} bg-white/80 backdrop-blur-sm p-1.5 rounded-full shadow-sm border border-green-100`}
                        animate={{ y: [0, -5, 0], x: [0, 3, 0] }}
                        transition={{ duration: 2, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" }}
                    >
                        <TbExchange className="w-5 h-5 text-green-600" />
                    </motion.div>
                </div>
            </motion.div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Calories */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-xl p-4 shadow-md border border-green-100 relative overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                >
                    <div className={`absolute ${isRTL ? "right-6" : "left-6"} -bottom-6 w-20 h-20 bg-orange-200/20 rounded-full blur-xl`}></div>

                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-1.5 rtl:space-x-reverse">
                            <div className="bg-orange-100 p-1 rounded-md">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                                        fill="#fdba74"
                                        fillOpacity="0.3"
                                        stroke="#f97316"
                                        strokeWidth="1.5"
                                    />
                                    <path d="M12 6V12L16 14" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span className="text-xs text-gray-600">{widget.calories?.label}</span>
                        </div>
                    </div>

                    <div className="relative h-12">
                        <div className="w-full bg-gray-100 rounded-full h-3 mb-2 overflow-hidden">
                            <motion.div
                                className="h-full rounded-full bg-gradient-to-r from-orange-400 to-amber-500 relative"
                                style={{ width: "65%" }}
                                initial={{ width: 0 }}
                                animate={{ width: "65%" }}
                                transition={{ duration: 1.2, ease: "easeOut" }}
                            >
                                <motion.div
                                    className={`absolute ${isRTL ? "left-0" : "right-0"} top-0 h-full w-1.5 bg-white/50`}
                                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                />
                            </motion.div>
                        </div>

                        <div className="flex justify-between items-center">
                            <div className="flex items-center mt-2">
                                <motion.div
                                    className={isRTL ? "mr-1" : "ml-1"}
                                    animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                                    transition={{ duration: 2.5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="#fdba74" fillOpacity="0.3" />
                                        <path
                                            d="M17.6569 6.34315C19.1571 7.84344 20 9.87827 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4"
                                            stroke="#f97316"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path d="M14.8816 9.32183L11.5193 12.3218L9.5193 10.3218" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </motion.div>
                                <span className="text-xs text-gray-600">{widget.calories?.value}</span>
                            </div>
                            <span className="text-xs font-medium text-orange-600">{widget.calories?.percentage}</span>
                        </div>
                    </div>
                </motion.div>

                {/* Distance */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-xl p-4 shadow-md border border-green-100 relative overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <div className={`absolute ${isRTL ? "left-6" : "right-6"} -bottom-6 w-20 h-20 bg-blue-200/20 rounded-full blur-xl`}></div>

                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-1.5 rtl:space-x-reverse">
                            <div className="bg-blue-100 p-1 rounded-md">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                        fill="#93c5fd"
                                        fillOpacity="0.3"
                                        stroke="#3b82f6"
                                        strokeWidth="1.5"
                                    />
                                    <path d="M16.5 14.5C16.5 14.5 15 16.5 12 16.5C9 16.5 7.5 14.5 7.5 14.5" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span className="text-xs text-gray-600">{widget.distance?.label}</span>
                        </div>
                    </div>

                    <div className="relative h-12">
                        <div className="w-full h-12">
                            <svg className="w-full h-9" viewBox="0 0 100 30" preserveAspectRatio="none">
                                <defs>
                                    <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="1" />
                                    </linearGradient>
                                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feGaussianBlur stdDeviation="1" result="blur" />
                                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                    </filter>
                                </defs>

                                <motion.path
                                    d="M 0,20 Q 10,10 20,15 T 40,20 T 60,10 T 80,15 T 100,8"
                                    fill="none"
                                    stroke="url(#pathGradient)"
                                    strokeWidth="2"
                                    filter="url(#glow)"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{ duration: 1.8, ease: "easeOut" }}
                                />

                                <motion.path
                                    d="M 0,20 Q 10,10 20,15 T 40,20 T 60,10 T 80,15 T 100,8 V 30 H 0 Z"
                                    fill="url(#pathGradient)"
                                    fillOpacity="0.1"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 1, delay: 0.8 }}
                                />

                                <motion.circle
                                    cx="100"
                                    cy="8"
                                    r="3"
                                    fill="#3b82f6"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 1.8, duration: 0.5, type: "spring" }}
                                />

                                <motion.circle
                                    cx="100"
                                    cy="8"
                                    r="6"
                                    fill="#3b82f6"
                                    opacity="0.3"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 0.3, scale: 1 }}
                                    transition={{ delay: 1.8, duration: 0.5 }}
                                >
                                    <animate attributeName="r" values="6;9;6" dur="2s" repeatCount="indefinite" />
                                    <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                                </motion.circle>
                            </svg>

                            <div className="flex justify-between text-[9px] text-gray-500 mt-1">
                                <span>{widget.distance?.min}</span>
                                <span>{widget.distance?.max}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Tips */}
            <motion.div
                className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg text-xs text-green-700 border border-green-100 shadow-sm relative overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                whileHover={{ scale: 1.01 }}
            >
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMxLjIgMCAyLjMuNSAzLjIgMS4zLjguOCAxLjMgMS45IDEuMyAzLjIgMCAxLjItLjUgMi4zLTEuMyAzLjItLjguOC0xLjkgMS4zLTMuMiAxLjMtMS4yIDAtMi4zLS41LTMuMi0xLjMtLjgtLjgtMS4zLTEuOS0xLjMtMy4yIDAtMS4yLjUtMi4zIDEuMy0zLjIuOC0uOCAxLjktMS4zIDMuMi0xLjN6IiBzdHJva2U9InJnYmEoMCwwLDAsMC4wMikiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-5"></div>

                <div className="flex items-start space-x-2 rtl:space-x-reverse">
                    <div className="bg-green-100 p-1.5 rounded-lg mt-0.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                                fill="#bbf7d0"
                                stroke="#16a34a"
                                strokeWidth="1.5"
                            />
                            <path d="M12 8V12L15 15" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div>
                        <p className="font-medium mb-1">{widget.tips?.title}</p>
                        <p>{widget.tips?.text}</p>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default StepsWidget;
